import { Controller } from "@hotwired/stimulus";
import { computePosition, offset, flip, shift, arrow, autoUpdate } from "@floating-ui/dom";

// Global tooltip state manager for intelligent behavior
class TooltipGlobalState {
  constructor() {
    this.visibleCount = 0;
    this.isFastMode = false;
    this.resetTimeout = null;
    this.fastModeResetDelay = 100; // 0.1 seconds
    this.visibleTooltips = new Set(); // Track currently visible tooltip controllers
    this.closingTooltips = new Set(); // Track tooltips currently in closing animation
  }

  // Called when a tooltip becomes visible
  onTooltipShow(tooltipController) {
    this.visibleTooltips.add(tooltipController);
    this.closingTooltips.delete(tooltipController); // Remove from closing if it was there
    this.visibleCount = this.visibleTooltips.size;
    if (this.visibleCount > 0 && !this.isFastMode) {
      this.isFastMode = true;
    }
    this.clearResetTimeout();
  }

  // Called when a tooltip starts hiding
  onTooltipStartHide(tooltipController) {
    this.visibleTooltips.delete(tooltipController);
    this.visibleCount = this.visibleTooltips.size;
  }

  // Called when a tooltip starts its closing animation
  onTooltipClosing(tooltipController) {
    this.closingTooltips.add(tooltipController);
  }

  // Called when a tooltip has fully closed
  onTooltipClosed(tooltipController) {
    this.closingTooltips.delete(tooltipController);

    // If no tooltips are visible or closing, start countdown to exit fast mode
    if (this.visibleCount === 0 && this.closingTooltips.size === 0) {
      this.startResetTimeout();
    }
  }

  // Hide all currently visible tooltips and interrupt closing animations
  hideAllTooltipsInstantly(exceptController) {
    // Instantly hide all visible tooltips
    const visibleToHide = [...this.visibleTooltips].filter((controller) => controller !== exceptController);
    visibleToHide.forEach((controller) => {
      controller._hideTooltip(true); // true = instant hide
    });

    // Instantly finish all closing animations
    const closingToHide = [...this.closingTooltips].filter((controller) => controller !== exceptController);
    closingToHide.forEach((controller) => {
      controller._finishClosingAnimation();
    });
  }

  // Check if we're in fast mode
  isInFastMode() {
    return this.isFastMode;
  }

  // Start timeout to reset fast mode
  startResetTimeout() {
    this.clearResetTimeout();
    this.resetTimeout = setTimeout(() => {
      this.isFastMode = false;
    }, this.fastModeResetDelay);
  }

  // Clear the reset timeout
  clearResetTimeout() {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = null;
    }
  }
}

// Global instance
const tooltipGlobalState = new TooltipGlobalState();

export default class extends Controller {
  // placement and offset can still be configured via data-tooltip-placement-value etc. if desired,
  // but will use defaults if the original HTML (using data-tooltip-*) doesn't provide them.
  static values = {
    placement: { type: String, default: "top" }, // Placement(s) of the tooltip, e.g., "top", "top-start", "top-end", "bottom", "bottom-start", "bottom-end", "left", "left-start", "left-end", "right", "right-start", "right-end"
    offset: { type: Number, default: 8 }, // Offset of the tooltip
    maxWidth: { type: Number, default: 200 }, // Default max width for tooltips
    delay: { type: Number, default: 0 }, // Delay before showing the tooltip (in ms)
    size: { type: String, default: "regular" }, // Size of the tooltip, e.g., "small", "regular", "large"
    animation: { type: String, default: "fade" }, // e.g., "fade", "origin", "fade origin", "none"
    trigger: { type: String, default: "auto" }, // space-separated: mouseenter, focus, click, or "auto" (auto-detects based on device)
    // tooltipContent and tooltipArrow are read directly from element attributes in connect()
  };

  _hasAnimationType(type) {
    return this.animationValue.split(" ").includes(type);
  }

  connect() {
    this.tooltipContent = this.element.getAttribute("data-tooltip-content") || "";
    this.showArrow = this.element.getAttribute("data-tooltip-arrow") !== "false";
    this.showTimeoutId = null;
    this.hideTimeoutId = null;
    this.isVisible = false;

    if (!this.tooltipContent) {
      console.warn("Tooltip initialized without data-tooltip-content", this.element);
      return;
    }

    this.tooltipElement = document.createElement("div");
    this.tooltipElement.className =
      "tooltip-content pointer-events-none wrap-break-word border rounded-lg border-neutral-700 absolute bg-blue-700 text-white py-1.5 px-3 z-[1000]";

    const sizeClasses = {
      small: "text-xs",
      regular: "text-sm",
      large: "text-base",
    };
    const sizeClass = sizeClasses[this.sizeValue] || sizeClasses.regular;
    this.tooltipElement.classList.add(sizeClass);

    // Always start transparent and hidden. Visibility/opacity managed by show/hide logic.
    this.tooltipElement.classList.add("opacity-0");
    this.tooltipElement.style.visibility = "hidden";

    // Base transition for all animations that might use opacity or transform
    if (this._hasAnimationType("fade") || this._hasAnimationType("origin")) {
      this.tooltipElement.classList.add("transition-all"); // Use transition-all for simplicity if combining
    }

    if (this._hasAnimationType("fade")) {
      // Ensure specific duration for opacity if not covered by a general one or if different
      this.tooltipElement.classList.add("duration-150"); // Default fade duration
    }
    if (this._hasAnimationType("origin")) {
      // Ensure specific duration for transform if not covered by a general one or if different
      this.tooltipElement.classList.add("duration-150", "ease-out"); // Default origin duration and ease
      this.tooltipElement.classList.add("scale-95"); // Initial state for origin animation
    }

    this.tooltipElement.innerHTML = this.tooltipContent;
    this.tooltipElement.style.maxWidth = `${this.maxWidthValue}px`;

    if (this.showArrow) {
      // Create arrow container with padding to prevent clipping at viewport edges
      this.arrowContainer = document.createElement("div");
      this.arrowContainer.className = "absolute z-[1000]";

      // Create the arrow element within the container
      this.arrowElement = document.createElement("div");
      this.arrowElement.className = "tooltip-arrow-element bg-neutral-800 w-2 h-2";
      this.arrowElement.style.transform = "rotate(45deg)";

      this.arrowContainer.appendChild(this.arrowElement);
      this.tooltipElement.appendChild(this.arrowContainer);
    }

    // Append target logic is handled in _showTooltip to ensure it's correct at showtime

    this._setupEventListeners();
  }

  disconnect() {
    this._cleanupTimeouts();
    if (this.cleanup) this.cleanup();
    if (this.tooltipElement && this.tooltipElement.parentNode) {
      this.tooltipElement.remove();
    }
  }

  _setupEventListeners() {
    const triggers = this._getTriggers();

    // Determine events based on triggers
    if (triggers.includes("mouseenter")) {
      this.element.addEventListener("mouseenter", this._onMouseEnter.bind(this));
      this.element.addEventListener("mouseleave", this._onMouseLeave.bind(this));
    }
    if (triggers.includes("focus")) {
      this.element.addEventListener("focus", this._onFocus.bind(this));
      this.element.addEventListener("blur", this._onBlur.bind(this));
    }
    if (triggers.includes("click")) {
      this.element.addEventListener("click", this._onClick.bind(this));
    }
  }

  _getTriggers() {
    if (this.triggerValue === "auto") {
      // Auto-detect based on device
      const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      return isTouchDevice ? ["click"] : ["mouseenter", "focus"];
    }
    return this.triggerValue.split(" ");
  }

  _onMouseEnter(event) {
    this._scheduleShow();
  }

  _onMouseLeave(event) {
    this._scheduleHide();
  }

  _onFocus(event) {
    this._scheduleShow();
  }

  _onBlur(event) {
    this._scheduleHide();
  }

  _onClick(event) {
    if (this.isVisible) {
      this._scheduleHide();
    } else {
      this._scheduleShow();
    }
  }

  _scheduleShow() {
    this._cleanupTimeouts();

    // Use delay from global state if in fast mode
    const showDelay = tooltipGlobalState.isInFastMode() ? 0 : this.delayValue;

    this.showTimeoutId = setTimeout(() => {
      // Before showing, hide all other tooltips instantly
      tooltipGlobalState.hideAllTooltipsInstantly(this);
      this._showTooltip();
    }, showDelay);
  }

  _scheduleHide() {
    this._cleanupTimeouts();
    this._hideTooltip();
  }

  _showTooltip() {
    if (this.isVisible) return;

    // Determine append target: dialog[open] if inside one, otherwise document.body
    const appendTarget = this.element.closest("dialog[open]") || document.body;
    appendTarget.appendChild(this.tooltipElement);

    // Start listening to scroll events on this element and ancestors
    this._startScrollListener();

    this.cleanup = autoUpdate(this.element, this.tooltipElement, () => this._updatePosition());

    // Trigger layout reflow if needed to ensure initial styles are applied before transition
    void this.tooltipElement.offsetHeight;

    // Make visible
    this.tooltipElement.style.visibility = "visible";
    this.tooltipElement.classList.remove("opacity-0");
    if (this._hasAnimationType("origin")) {
      this.tooltipElement.classList.remove("scale-95");
      this.tooltipElement.classList.add("scale-100");
    }

    this.isVisible = true;
    tooltipGlobalState.onTooltipShow(this);
  }

  // instant: if true, skip closing animation and just remove immediately
  _hideTooltip(instant = false) {
    if (!this.isVisible) return;

    tooltipGlobalState.onTooltipStartHide(this);

    this._stopScrollListener();

    if (instant || this.animationValue === "none") {
      this._finishHiding();
    } else {
      // Start closing animation
      tooltipGlobalState.onTooltipClosing(this);

      this.tooltipElement.classList.add("opacity-0");
      if (this._hasAnimationType("origin")) {
        this.tooltipElement.classList.remove("scale-100");
        this.tooltipElement.classList.add("scale-95");
      }

      // Wait for transition to complete before removing
      // Use a timeout matching the transition duration
      const duration = 150; // matches duration-150
      setTimeout(() => {
        this._finishHiding();
        tooltipGlobalState.onTooltipClosed(this);
      }, duration);
    }

    this.isVisible = false;
  }

  // Instantly finish the closing animation (called by global state)
  _finishClosingAnimation() {
    if (!this.isVisible && this.tooltipElement && this.tooltipElement.parentNode) {
      // Just finish hiding immediately
      this._finishHiding();
      tooltipGlobalState.onTooltipClosed(this);
    }
  }

  _finishHiding() {
    this.tooltipElement.style.visibility = "hidden";
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    if (this.tooltipElement.parentNode) {
      this.tooltipElement.remove();
    }
  }

  _updatePosition() {
    const middlewareArray = [offset(this.offsetValue), flip(), shift({ padding: 8 })];

    if (this.showArrow && this.arrowElement) {
      middlewareArray.push(arrow({ element: this.arrowElement, padding: 4 }));
    }

    computePosition(this.element, this.tooltipElement, {
      placement: this.placementValue,
      middleware: middlewareArray,
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.tooltipElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      // Update arrow position and styling
      if (this.showArrow && this.arrowElement && middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        // Determine which side of the tooltip the arrow should be on
        const staticSide = {
          top: "bottom",
          right: "left",
          bottom: "top",
          left: "right",
        }[placement.split("-")[0]];

        // Position arrow
        Object.assign(this.arrowContainer.style, {
          left: arrowX != null ? `${arrowX}px` : "",
          top: arrowY != null ? `${arrowY}px` : "",
          right: "",
          bottom: "",
          [staticSide]: "-4px",
        });

        // Update border visibility based on which side the arrow is on
        // The arrow is rotated 45deg, so we show/hide borders appropriately
        const borderColor = "#404040"; // neutral-700
        if (staticSide === "bottom") {
          this.arrowElement.style.borderTop = "none";
          this.arrowElement.style.borderLeft = "none";
          this.arrowElement.style.borderRight = `1px solid ${borderColor}`;
          this.arrowElement.style.borderBottom = `1px solid ${borderColor}`;
        } else if (staticSide === "top") {
          this.arrowElement.style.borderTop = `1px solid ${borderColor}`;
          this.arrowElement.style.borderLeft = `1px solid ${borderColor}`;
          this.arrowElement.style.borderRight = "none";
          this.arrowElement.style.borderBottom = "none";
        } else if (staticSide === "left") {
          this.arrowElement.style.borderTop = "none";
          this.arrowElement.style.borderLeft = `1px solid ${borderColor}`;
          this.arrowElement.style.borderRight = "none";
          this.arrowElement.style.borderBottom = `1px solid ${borderColor}`;
        } else if (staticSide === "right") {
          this.arrowElement.style.borderTop = `1px solid ${borderColor}`;
          this.arrowElement.style.borderLeft = "none";
          this.arrowElement.style.borderRight = `1px solid ${borderColor}`;
          this.arrowElement.style.borderBottom = "none";
        }
      }
    });
  }

  _startScrollListener() {
    this._onScrollBound = this._onScroll.bind(this);
    // Listen to scroll events on the element and all scrollable ancestors
    let currentElement = this.element;
    while (currentElement) {
      currentElement.addEventListener("scroll", this._onScrollBound, { passive: true });
      currentElement = currentElement.parentElement;
    }
    // Also listen to window scroll
    window.addEventListener("scroll", this._onScrollBound, { passive: true });
  }

  _stopScrollListener() {
    if (!this._onScrollBound) return;
    let currentElement = this.element;
    while (currentElement) {
      currentElement.removeEventListener("scroll", this._onScrollBound);
      currentElement = currentElement.parentElement;
    }
    window.removeEventListener("scroll", this._onScrollBound);
    this._onScrollBound = null;
  }

  _onScroll() {
    // Check if element is still visible in viewport
    const rect = this.element.getBoundingClientRect();
    const isVisible =
      rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;

    if (!isVisible) {
      this._hideTooltip(true);
    }
  }

  _cleanupTimeouts() {
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }
}
